import { useState, useEffect, useCallback } from 'react';
import { useChat } from '../../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { FormulaTooltip, resolveMetricKey } from '../FormulaTooltip';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, FunnelChart, Funnel, LabelList,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend,
  Sankey, Layer, Rectangle
} from 'recharts';
import { 
  Users, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  UserCheck,
  UserX,
  Clock,
  Star,
  Crown,
  Diamond,
  Target,
  RefreshCw,
  ChevronRight,
  ArrowRight,
  Sparkles,
  Activity,
  AlertCircle,
  CheckCircle,
  XCircle,
  Zap,
  ZoomIn
} from 'lucide-react';
import axios from 'axios';
import DrillDownModal from '../DrillDownModal';
import ChartZoomModal, { ChartZoomButton } from '../ChartZoomModal';
import DeepAIInsights from '../DeepAIInsights';
import DashboardFilters from '../DashboardFilters';
import { formatNumber, formatCurrency } from '../../lib/formatters';
import { ExportButton } from '../ExportUtils';
import { AskAIButton } from '../AskAIButton';
import { LaunchCampaignButton } from '../LaunchCampaignButton';
import { Rocket } from 'lucide-react';

import { API_BASE_URL as API } from '../../utils/apiConfig';

const COLORS = {
  primary: '#8B5CF6',
  secondary: '#EC4899',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#3B82F6',
  gray: '#6B7280',
  teal: '#14B8A6'
};

const COHORT_COLORS = ['#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B', '#3B82F6', '#EF4444', '#6366F1', '#84CC16'];

// Insight Card Component with severity styling
const InsightCard = ({ title, insight, recommendation, severity = "info", onDrillDown, brandId, segmentName, customers, onCampaignCreated }) => {
  const severityStyles = {
    critical: { bg: "from-red-500/20 to-red-600/10", border: "border-red-500/30", icon: AlertCircle, iconColor: "text-red-400", priority: "P0" },
    warning: { bg: "from-amber-500/20 to-amber-600/10", border: "border-amber-500/30", icon: AlertTriangle, iconColor: "text-amber-400", priority: "P1" },
    success: { bg: "from-emerald-500/20 to-emerald-600/10", border: "border-emerald-500/30", icon: CheckCircle, iconColor: "text-emerald-400", priority: "P2" },
    info: { bg: "from-blue-500/20 to-blue-600/10", border: "border-blue-500/30", icon: Sparkles, iconColor: "text-blue-400", priority: "P2" }
  };
  
  const style = severityStyles[severity] || severityStyles.info;
  const Icon = style.icon;
  
  return (
    <Card 
      className={`bg-gradient-to-br ${style.bg} ${style.border} border hover:shadow-lg transition-all group`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-background/50 ${style.iconColor}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm mb-1">{title}</h4>
            <p className="text-xs text-muted-foreground mb-2">{insight}</p>
            {recommendation && (
              <p className="text-xs text-foreground/80 bg-background/30 p-2 rounded mb-2">
                <strong>Action:</strong> {recommendation}
              </p>
            )}
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-7 text-xs gap-1"
                onClick={onDrillDown}
              >
                <ZoomIn className="w-3 h-3" />
                View Details
              </Button>
              {brandId && recommendation && (
                <LaunchCampaignButton
                  segmentName={segmentName || title}
                  customers={customers || 0}
                  brandId={brandId}
                  recommendation={{
                    action: recommendation,
                    priority: style.priority
                  }}
                  onCampaignCreated={onCampaignCreated}
                  size="sm"
                  className="h-7 text-xs bg-violet-500 hover:bg-violet-600"
                  buttonText="Activate"
                />
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Cohort Card with detailed metrics
const CohortCard = ({ cohort, onClick, isSelected, brandId, onCampaignCreated }) => {
  const actionColors = {
    SAVE: "bg-red-500/20 text-red-400 border-red-500/30",
    RETAIN: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    GROW: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    CONVERT: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    REACTIVATE: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    NURTURE: "bg-teal-500/20 text-teal-400 border-teal-500/30",
    IGNORE: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    MONITOR: "bg-purple-500/20 text-purple-400 border-purple-500/30"
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    return `₹${value?.toLocaleString()}`;
  };

  // Use imported formatNumber from lib/formatters

  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-lg ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={() => onClick(cohort)}
      data-testid={`cohort-${cohort.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown'}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">{cohort.name || 'Unknown'}</h3>
            <p className="text-xs text-muted-foreground truncate">{cohort.description}</p>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full border whitespace-nowrap ml-2 ${actionColors[cohort.dominant_action] || actionColors.MONITOR}`}>
            {cohort.dominant_action}
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="p-2 rounded-lg bg-muted/50">
            <p className="text-xs text-muted-foreground">Customers</p>
            <p className="text-lg font-bold truncate">{formatNumber(cohort.customers)}</p>
            <p className="text-xs text-muted-foreground">{cohort.customers_pct}%</p>
          </div>
          <div className="p-2 rounded-lg bg-muted/50">
            <p className="text-xs text-muted-foreground">Revenue</p>
            <p className="text-lg font-bold truncate">{formatCurrency(cohort.revenue)}</p>
            <p className="text-xs text-muted-foreground">{cohort.revenue_pct}%</p>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-xs text-muted-foreground">Avg Revenue</p>
            <p className="text-sm font-semibold">{formatCurrency(cohort.avg_revenue)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Avg Visits</p>
            <p className="text-sm font-semibold">{cohort.avg_visits}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Recency</p>
            <p className="text-sm font-semibold">{cohort.avg_recency_days}d</p>
          </div>
        </div>
        
        {/* Churn Risk Mini Bar */}
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground mb-1">Churn Risk Distribution</p>
          <div className="flex h-2 rounded-full overflow-hidden">
            <div 
              className="bg-emerald-500" 
              style={{ width: `${(cohort.churn_breakdown?.Low || 0) / cohort.customers * 100}%` }}
              title={`Low: ${cohort.churn_breakdown?.Low || 0}`}
            />
            <div 
              className="bg-amber-500" 
              style={{ width: `${(cohort.churn_breakdown?.Medium || 0) / cohort.customers * 100}%` }}
              title={`Medium: ${cohort.churn_breakdown?.Medium || 0}`}
            />
            <div 
              className="bg-orange-500" 
              style={{ width: `${(cohort.churn_breakdown?.High || 0) / cohort.customers * 100}%` }}
              title={`High: ${cohort.churn_breakdown?.High || 0}`}
            />
            <div 
              className="bg-red-500" 
              style={{ width: `${(cohort.churn_breakdown?.Critical || 0) / cohort.customers * 100}%` }}
              title={`Critical: ${cohort.churn_breakdown?.Critical || 0}`}
            />
          </div>
        </div>
        
        {/* Launch Campaign Button */}
        <div className="mt-3 pt-3 border-t border-border">
          <LaunchCampaignButton
            cohortName={cohort.name}
            customers={cohort.customers}
            brandId={brandId}
            recommendation={{
              action: `${cohort.dominant_action}: Target ${cohort.name} segment`,
              priority: cohort.dominant_action === 'SAVE' ? 'P0' : cohort.dominant_action === 'CONVERT' ? 'P1' : 'P2',
              impact: formatCurrency(cohort.revenue * 0.1)
            }}
            onCampaignCreated={onCampaignCreated}
            size="sm"
            className="w-full bg-violet-500 hover:bg-violet-600"
            buttonText={`Launch ${cohort.dominant_action} Campaign`}
          />
        </div>
      </CardContent>
    </Card>
  );
};

// KPI Summary Card
const KPISummary = ({ label, value, subtext, icon: Icon, color = "primary", onClick }) => (
  <Card 
    className="cursor-pointer hover:shadow-lg transition-all group"
    onClick={onClick}
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-xl bg-${color}-500/20`}>
          <Icon className={`w-5 h-5 text-${color}-400`} style={{ color: COLORS[color] }} />
        </div>
        <div className="flex-1">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
      </div>
    </CardContent>
  </Card>
);

export default function CohortMigration() {
  const { selectedBrand } = useChat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [drillDown, setDrillDown] = useState({ open: false, type: null, data: null, title: '' });
  const [isLiveData, setIsLiveData] = useState(false);
  const [filters, setFilters] = useState({ dateRange: 'lifetime', city: 'all', store: 'all' });
  const [filterOptions, setFilterOptions] = useState({ cities: [], stores: [] });
  const [chartZoom, setChartZoom] = useState({ open: false, type: '', data: [], title: '', config: {} });

  // Chart zoom handlers
  const openChartZoom = (chartType, chartData, chartTitle, chartConfig = {}) => {
    setChartZoom({ open: true, type: chartType, data: chartData, title: chartTitle, config: chartConfig });
  };
  const closeChartZoom = () => setChartZoom({ open: false, type: '', data: [], title: '', config: {} });

  // Check for live data
  const getDbBrandId = useCallback(() => {
    return selectedBrand?.db_brand_id || selectedBrand?.settings?.db_brand_id || null;
  }, [selectedBrand]);

  const fetchData = useCallback(async () => {
    if (!selectedBrand) return;
    
    setLoading(true);
    setError(null);
    
    // Check for live Data Bridge - mark as live if brand has db_brand_id
    const dbBrandId = getDbBrandId();
    // Add cache-busting to ensure fresh data
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Use original API for proper data structure
    try {
      const response = await axios.post(
        `${API}/api/analytics/cohort-migration?_t=${Date.now()}&_rid=${requestId}`,
        { brand_id: selectedBrand.brand_id, filters },
        { 
          withCredentials: true,
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'X-Request-ID': requestId
          }
        }
      );
      
      // Force new object reference
      setData({...response.data.data});
      setIsLiveData(response.data.data?.source === 'live_data_bridge' || !!dbBrandId);
      
      // Extract filter options from response
      if (response.data.data?.filter_context) {
        setFilterOptions({
          cities: response.data.data.filter_context.cities || [],
          stores: response.data.data.filter_context.stores || []
        });
      }
    } catch (err) {
      console.error('Cohort Migration data error:', err);
      setError('Failed to load Cohort Migration data');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, getDbBrandId, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = () => {
    if (!data) return;
    
    // Helper to format CSV values properly
    const formatCsvValue = (val) => {
      if (val === null || val === undefined) return '';
      const strVal = String(val);
      if (strVal.includes(',') || strVal.includes('"')) {
        return `"${strVal.replace(/"/g, '""')}"`;
      }
      return strVal;
    };
    
    const csvContent = [
      ['Cohort', 'Customers', 'Revenue (₹)', 'Avg Revenue (₹)', 'Avg Visits', 'Action'],
      ...(data.cohorts || []).map(c => [
        formatCsvValue(c.name), 
        c.customers || 0, 
        Math.round(c.revenue || 0), 
        Math.round(c.avg_revenue || 0), 
        (c.avg_visits || 0).toFixed(1), 
        formatCsvValue(c.dominant_action)
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cohort_migration_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const openDrillDown = (type, value, title, context = {}) => {
    setDrillDown({ open: true, type, data: { value, context }, title });
  };

  const closeDrillDown = () => {
    setDrillDown({ open: false, type: null, data: null, title: '' });
  };

  const handleCohortClick = (cohort) => {
    setSelectedCohort(cohort);
    openDrillDown('cohort', cohort.name, `${cohort.name} Cohort Details`, { cohort });
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    return `₹${value?.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="cohort-migration-loading">
        <div className="flex flex-col items-center gap-4">
          <Users className="w-12 h-12 text-primary animate-pulse" />
          <p className="text-muted-foreground">Analyzing customer cohorts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={fetchData}>Retry</Button>
        </Card>
      </div>
    );
  }

  const summary = data?.summary || {};
  const cohorts = data?.cohorts || [];
  const funnel = data?.funnel || [];
  const insights = data?.insights || {};
  const clvDistribution = data?.clv_distribution || [];
  const churnDistribution = data?.churn_distribution || [];
  const actionDistribution = data?.action_distribution || [];
  const atvSplit = data?.atv_split || {};

  return (
    <div className="space-y-6" data-testid="cohort-migration">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-pink-500 to-violet-500">
              <Users className="w-6 h-6 text-white" />
            </div>
            Cohort & Customer Migration
            {data?.data_as_of && (
              <Badge className="ml-2 bg-blue-500/80 text-white">
                <Clock className="w-3 h-3 mr-1" />
                Data as of: {data.data_as_of}
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground mt-1">
            {`${data?.summary?.total_customers?.toLocaleString() || 0} customers analyzed from real transaction data`}
          </p>
        </div>
      </div>
      
      {/* Dashboard Filters */}
      <DashboardFilters
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
        onRefresh={fetchData}
        onExport={handleExport}
        loading={loading}
      />

      {/* Full Report Export Button */}
      <div className="flex justify-end">
        <ExportButton 
          brandId={selectedBrand?.brand_id}
          dashboardType="customer_segmentation"
          label="Download Full Report"
          filename={`cohort_migration_${selectedBrand?.name || 'export'}.csv`}
        />
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPISummary
          label="Total Customers"
          value={summary.total_customers?.toLocaleString()}
          subtext="Transacting"
          icon={Users}
          color="primary"
          onClick={() => openDrillDown('kpi', 'total_customers', 'Total Customers Breakdown')}
        />
        <KPISummary
          label="Total Revenue"
          value={formatCurrency(summary.total_revenue)}
          subtext={`ATV: ₹${summary.overall_atv?.toLocaleString()}`}
          icon={TrendingUp}
          color="success"
          onClick={() => openDrillDown('kpi', 'revenue', 'Revenue by Cohort')}
        />
        <KPISummary
          label="One-Timer Rate"
          value={`${summary.one_timer_rate}%`}
          subtext="Target: <40%"
          icon={UserX}
          color="warning"
          onClick={() => openDrillDown('cohort', 'One-Timers', 'One-Timer Analysis')}
        />
        <KPISummary
          label="Repeat Rate"
          value={`${summary.repeat_rate}%`}
          subtext="Target: >60%"
          icon={UserCheck}
          color="info"
          onClick={() => openDrillDown('kpi', 'repeat_rate', 'Repeat Customer Analysis')}
        />
        <KPISummary
          label="Lapsed Rate"
          value={`${summary.lapsed_rate}%`}
          subtext="90+ days inactive"
          icon={Clock}
          color="danger"
          onClick={() => openDrillDown('kpi', 'lapsed', 'Lapsed Customer Analysis')}
        />
        <KPISummary
          label="Above ATV"
          value={`${atvSplit.above?.count?.toLocaleString()}`}
          subtext={`>${formatCurrency(atvSplit.threshold)}`}
          icon={Star}
          color="teal"
          onClick={() => openDrillDown('kpi', 'atv_split', 'ATV Split Analysis')}
        />
      </div>

      {/* AI Insights Row */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <InsightCard
          title={insights.one_timers?.title}
          insight={insights.one_timers?.insight}
          recommendation={insights.one_timers?.recommendation}
          severity={insights.one_timers?.severity}
          onDrillDown={() => openDrillDown('cohort', 'One-Timers', 'One-Timer Deep Dive')}
          brandId={selectedBrand?.brand_id}
          segmentName="One-Timers"
          customers={cohorts.find(c => c.name === 'One-Timers')?.customers || 0}
        />
        <InsightCard
          title={insights.lapsed?.title}
          insight={insights.lapsed?.insight}
          recommendation={insights.lapsed?.recommendation}
          severity={insights.lapsed?.severity}
          onDrillDown={() => openDrillDown('kpi', 'lapsed', 'Lapsed Customer Deep Dive')}
          brandId={selectedBrand?.brand_id}
          segmentName="Lapsed Customers"
          customers={cohorts.find(c => c.name?.includes('Lapsed'))?.customers || 0}
        />
        <InsightCard
          title={insights.high_value_risk?.title}
          insight={insights.high_value_risk?.insight}
          recommendation={insights.high_value_risk?.recommendation}
          severity={insights.high_value_risk?.severity}
          onDrillDown={() => openDrillDown('kpi', 'high_value_risk', 'High-Value At-Risk Analysis')}
          brandId={selectedBrand?.brand_id}
          segmentName="High-Value At-Risk"
          customers={parseInt(data?.kpis?.lapsed?.value?.replace(/[^0-9]/g, '') || 0)}
        />
      </div>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="cohorts">Cohort Details</TabsTrigger>
          <TabsTrigger value="funnel">Customer Funnel</TabsTrigger>
          <TabsTrigger value="actions">Action Matrix</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Customer % by Cohort */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-violet-500" />
                  Customer % by Cohort
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={cohorts.map(c => ({
                        name: c.name,
                        value: c.customers,
                        pct: ((c.customers / (summary?.total_customers || 1)) * 100).toFixed(1)
                      })).sort((a, b) => b.value - a.value)}
                      dataKey="value"
                      nameKey="name"
                      cx="40%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      label={({ cx, cy, midAngle, innerRadius, outerRadius, pct, index }) => {
                        const RADIAN = Math.PI / 180;
                        const radius = outerRadius + 25;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        return (
                          <text
                            x={x}
                            y={y}
                            fill="currentColor"
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                            className="text-xs"
                          >
                            {`${pct}%`}
                          </text>
                        );
                      }}
                      labelLine={true}
                    >
                      {cohorts.map((_, idx) => (
                        <Cell key={idx} fill={COHORT_COLORS[idx % COHORT_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(v, name, props) => [
                        `${v?.toLocaleString()} (${props.payload.pct}%)`,
                        props.payload.name
                      ]} 
                    />
                    <Legend 
                      layout="vertical" 
                      align="right" 
                      verticalAlign="middle"
                      wrapperStyle={{ paddingLeft: '10px' }}
                      formatter={(value, entry) => (
                        <span className="text-xs">{entry.payload.name}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Revenue Contribution by Cohort */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  Revenue Contribution by Cohort
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={cohorts} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      width={90} 
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => v.length > 12 ? v.slice(0, 12) + '...' : v}
                    />
                    <Tooltip 
                      formatter={(v, name, props) => [
                        `${v}% (₹${(props.payload.revenue / 10000000).toFixed(1)}Cr)`,
                        'Revenue Share'
                      ]} 
                    />
                    <Bar dataKey="revenue_pct" radius={[0, 4, 4, 0]}>
                      {cohorts.map((_, idx) => (
                        <Cell key={idx} fill={COHORT_COLORS[idx % COHORT_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Action Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-500" />
                  Action Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={actionDistribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="action" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => v?.toLocaleString()} />
                    <Bar dataKey="count" fill={COLORS.primary} radius={[4, 4, 0, 0]}>
                      {actionDistribution.map((_, idx) => (
                        <Cell key={idx} fill={COHORT_COLORS[idx % COHORT_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* ATV Split Visualization */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-4 h-4 text-teal-500" />
                Above vs Below ATV Split (Threshold: ₹{atvSplit.threshold?.toLocaleString()})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Above ATV</span>
                    <span className="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-400">HIGH VALUE</span>
                  </div>
                  <p className="text-3xl font-bold text-emerald-400">{atvSplit.above?.count?.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">customers • {formatCurrency(atvSplit.above?.revenue)} revenue</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    These customers spend more than average. Focus on retention and upselling premium products.
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Below ATV</span>
                    <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-400">GROWTH OPP</span>
                  </div>
                  <p className="text-3xl font-bold text-amber-400">{atvSplit.below?.count?.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">customers • {formatCurrency(atvSplit.below?.revenue)} revenue</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Opportunity to increase basket size through cross-sell, bundling, and targeted promotions.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cohorts Tab */}
        <TabsContent value="cohorts" className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cohorts.map((cohort, idx) => (
              <CohortCard
                key={cohort.name}
                cohort={cohort}
                onClick={handleCohortClick}
                isSelected={selectedCohort?.name === cohort.name}
                brandId={selectedBrand?.brand_id}
                onCampaignCreated={(campaign) => {
                  console.log('Campaign created:', campaign);
                  // Optionally show toast or redirect
                }}
              />
            ))}
          </div>
          
          {/* Cohort Insights */}
          <div className="grid md:grid-cols-2 gap-4">
            <InsightCard
              title={insights.champions?.title}
              insight={insights.champions?.insight}
              recommendation={insights.champions?.recommendation}
              severity={insights.champions?.severity}
              onDrillDown={() => openDrillDown('cohort', 'Repeat (21+)', 'Champions Analysis')}
              brandId={selectedBrand?.brand_id}
              segmentName="Champions (21+ visits)"
              customers={cohorts.find(c => c.name?.includes('21+'))?.customers || 0}
            />
            <InsightCard
              title={insights.atv_split?.title}
              insight={insights.atv_split?.insight}
              recommendation={insights.atv_split?.recommendation}
              severity={insights.atv_split?.severity}
              onDrillDown={() => openDrillDown('kpi', 'atv', 'ATV Analysis')}
              brandId={selectedBrand?.brand_id}
              segmentName="High ATV Customers"
              customers={parseInt(data?.kpis?.total_customers?.value?.replace(/[^0-9]/g, '') || 0) * 0.2}
            />
          </div>
        </TabsContent>

        {/* Funnel Tab */}
        <TabsContent value="funnel" className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Customer Lifecycle Funnel</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {funnel.map((stage, idx) => (
                  <div 
                    key={stage.stage}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => openDrillDown('funnel_stage', stage.stage, `${stage.stage} Analysis`)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{stage.stage}</span>
                      <span className="text-sm text-muted-foreground">{stage.count?.toLocaleString()} ({stage.pct}%)</span>
                    </div>
                    <div className="h-8 rounded-lg bg-muted overflow-hidden">
                      <div 
                        className="h-full rounded-lg transition-all duration-500"
                        style={{ 
                          width: `${stage.pct}%`,
                          backgroundColor: COHORT_COLORS[idx % COHORT_COLORS.length]
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Funnel Insights */}
              <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-pink-500/10 border border-violet-500/20">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-violet-400 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm mb-1">Funnel Analysis Insight</h4>
                    <p className="text-xs text-muted-foreground">
                      Your conversion funnel shows {funnel[1]?.pct}% one-timers at the top. 
                      Only {funnel[funnel.length - 1]?.pct}% reach champion status. 
                      Focus on the {funnel[1]?.stage} → {funnel[2]?.stage} transition 
                      to maximize customer lifetime value. Each 1% improvement here could mean 
                      ₹{((summary.total_revenue || 0) * 0.01 / 10000000).toFixed(2)}Cr additional revenue.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Migration Matrix */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowRight className="w-4 h-4" />
                Customer Migration Patterns (Projected)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="px-4 py-3 text-left text-sm font-semibold">From Cohort</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-emerald-400">Stay</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-blue-400">Upgrade</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-amber-400">Downgrade</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-red-400">Churn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.migration_matrix?.map((row, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/20">
                        <td className="px-4 py-3 font-medium">{row.from_cohort}</td>
                        <td className="px-4 py-3 text-right text-emerald-400">{row.stay?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-blue-400">{row.upgrade?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-amber-400">{row.downgrade?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-red-400">{row.churn?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                * Migration patterns are projected based on historical behavior analysis
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Actions Tab */}
        <TabsContent value="actions" className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {actionDistribution.slice(0, 8).map((action, idx) => (
              <Card 
                key={action.action}
                className="cursor-pointer hover:shadow-lg transition-all"
                onClick={() => openDrillDown('action', action.action, `${action.action} Action List`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">{action.action}</span>
                    <span 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COHORT_COLORS[idx % COHORT_COLORS.length] }}
                    />
                  </div>
                  <p className="text-2xl font-bold">{action.count?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">customers</p>
                  <p className="text-sm text-muted-foreground mt-1">{formatCurrency(action.revenue)} potential</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Action Recommendations */}
          <Card className="bg-gradient-to-r from-violet-500/10 via-pink-500/5 to-amber-500/10 border-violet-500/20">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold mb-3">AI Action Recommendations</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="p-3 rounded-lg bg-background/50">
                      <p className="font-medium text-red-400 mb-1">🔴 SAVE (Highest Priority)</p>
                      <p className="text-xs text-muted-foreground">
                        High-value customers at risk. Immediate personal outreach, exclusive offers, VIP treatment.
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-background/50">
                      <p className="font-medium text-blue-400 mb-1">🔵 CONVERT</p>
                      <p className="text-xs text-muted-foreground">
                        One-timers with potential. D+7 follow-up, related product recommendations, loyalty benefits education.
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-background/50">
                      <p className="font-medium text-emerald-400 mb-1">🟢 GROW</p>
                      <p className="text-xs text-muted-foreground">
                        Stable customers ready for upsell. Cross-sell opportunities, premium tier upgrade campaigns.
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-background/50">
                      <p className="font-medium text-amber-400 mb-1">🟡 REACTIVATE</p>
                      <p className="text-xs text-muted-foreground">
                        Lapsed customers with history. "We miss you" campaigns with strong incentives, time-limited offers.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Overall AI Insight */}
      <Card className="bg-gradient-to-r from-violet-500/10 via-pink-500/5 to-amber-500/10 border-violet-500/20">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">{insights.overall?.title}</h3>
              <p className="text-sm text-muted-foreground">{insights.overall?.insight}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deep AI Insights */}
      <DeepAIInsights
        brandId={selectedBrand?.brand_id}
        brandName={selectedBrand?.name}
        dashboardType="cohort_migration"
        dashboardData={data}
        kpis={{
          total_customers: { value: data?.totals?.total_customers || 0 },
          total_revenue: { value: data?.totals?.total_revenue || 0 }
        }}
      />

      {/* Drill Down Modal */}
      <DrillDownModal
        open={drillDown.open}
        onClose={closeDrillDown}
        drillType={drillDown.type}
        drillData={drillDown.data}
        title={drillDown.title}
        brandId={selectedBrand?.brand_id}
      />

      {/* Chart Zoom Modal */}
      <ChartZoomModal
        open={chartZoom.open}
        onClose={closeChartZoom}
        chartType={chartZoom.type}
        chartData={chartZoom.data}
        chartTitle={chartZoom.title}
        chartConfig={chartZoom.config}
        brandId={selectedBrand?.brand_id}
        dashboardType="cohort_migration"
      />
    </div>
  );
}
